import { useState } from "react";
import { apiUrl } from "utils/apiUrl";
import { clientSafeMessage } from "utils/clientSafeMessage";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { clearAdminSession } from "utils/adminSession";
import { clearClientSession, saveClientSession } from "utils/clientSession";

const INPUT_CLASS =
  "mt-2 mb-3 flex h-12 w-full items-center justify-center rounded-xl border border-white/20 bg-white/5 p-3 text-sm text-white outline-none placeholder:text-gray-400";

function safeReturnPath(raw) {
  const path = String(raw || "").trim();
  if (!path.startsWith("/") || path.startsWith("//")) return null;
  if (path.startsWith("/SignIn") || path.startsWith("/SignUp")) return null;
  return path;
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [errorText, setErrorText] = useState("");
  const [loading, setLoading] = useState(false);
  const returnAfterLogin =
    safeReturnPath(searchParams.get("returnTo")) ||
    safeReturnPath(location.state?.from);
  const [showVerifyEmailModal, setShowVerifyEmailModal] = useState(false);
  const [verifyEmailModalText, setVerifyEmailModalText] = useState(
    "Please verify your e-mail from the verification message before accessing the dashboard.",
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorText("");
    const idTrim = identifier.trim();
    if (!idTrim && !password) {
      setErrorText("Enter your username or e-mail and your password.");
      return;
    }
    if (!idTrim) {
      setErrorText("Enter your username or e-mail.");
      return;
    }
    if (!password) {
      setErrorText("Enter your password.");
      return;
    }
    if (!recaptchaToken) {
      setErrorText("Please complete the reCAPTCHA.");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(apiUrl("/api/login"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          identifier: idTrim,
          password,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.ok) {
        if (
          response.status === 403 &&
          typeof data.message === "string" &&
          /verify.*e-?mail/i.test(data.message)
        ) {
          setVerifyEmailModalText(data.message);
          setShowVerifyEmailModal(true);
          return;
        }
        const msg = String(data.message || "").toLowerCase();
        if (/invalid user or password/i.test(msg)) {
          setErrorText(
            "Invalid user or password. Use the account you created on Sign Up.",
          );
        } else {
          setErrorText(
            clientSafeMessage(
              data.message,
              "Could not sign in. Please check your user or e-mail and password.",
            ),
          );
        }
        return;
      }

      if (data.isAdmin) {
        clearClientSession();
        clearAdminSession();
        setErrorText(
          "This account is for team administrators only. Use the administrator sign-in page (not this client form).",
        );
        return;
      }

      clearAdminSession();
      saveClientSession({
        clientId: data.clientId,
        firstName:
          typeof data.firstName === "string" ? data.firstName : "",
        lastName: typeof data.lastName === "string" ? data.lastName : "",
        email: typeof data.email === "string" ? data.email : "",
        at: Date.now(),
      });
      navigate(returnAfterLogin || "/client/dashboard", { replace: true });
    } catch {
      setErrorText("Network error. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative z-30 my-1 flex h-full w-full items-start justify-center px-2 md:mx-0 md:px-0 lg:items-start lg:justify-start">
      <div className="mt-0 w-full max-w-full flex-col items-center md:pl-4 lg:pl-0 xl:max-w-[420px]">
        <h4 className="mb-8 w-full text-center text-4xl font-bold text-white">
          Sign In
        </h4>
        {returnAfterLogin?.startsWith("/PayUSDT") ? (
          <p className="-mt-4 mb-6 text-center text-sm text-brand-200">
            Sign in to open the payment page and buy your slot.
          </p>
        ) : null}

        <form onSubmit={handleSubmit} noValidate>
          <label className="ml-1.5 text-sm font-medium text-gray-100">
            User or E-mail*
            <input
              name="identifier"
              type="text"
              autoComplete="username"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="username or you@example.com"
              className={INPUT_CLASS}
            />
          </label>

          <label className="ml-1.5 text-sm font-medium text-gray-100">
            Password*
            <input
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
              className={INPUT_CLASS}
            />
          </label>

          <div className="mb-6 flex justify-end px-1.5">
            <Link
              to="/PasswordRecovery"
              className="text-sm font-medium text-brand-300 hover:text-brand-200"
            >
              Forgot your password?
            </Link>
          </div>

          <div className="mb-4 rounded-xl border border-white/15 bg-white/5 p-2">
            <p className="mb-2 text-center text-sm font-medium text-gray-100">
              reCAPTCHA*
            </p>
            <div className="flex justify-center">
              <div className="origin-center scale-[0.93]">
                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ""}
                  onChange={(token) => setRecaptchaToken(token || "")}
                />
              </div>
            </div>
          </div>

          {errorText && (
            <p className="mb-3 text-sm text-red-400">{errorText}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="linear w-full rounded-xl bg-brand-500 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 disabled:opacity-60 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
          >
            {loading ? "Signing in…" : "SignIn"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-400">
          No account yet?{" "}
          <Link
            to={
              returnAfterLogin
                ? `/SignUp?returnTo=${encodeURIComponent(returnAfterLogin)}`
                : "/SignUp"
            }
            className="font-medium text-brand-300 hover:text-brand-200"
          >
            SignUp
          </Link>
        </p>

        {showVerifyEmailModal && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 px-4">
            <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0f1a4b] p-6 text-white shadow-2xl">
              <h3 className="text-center text-lg font-bold">
                E-mail verification required
              </h3>
              <p className="mt-3 text-center text-sm leading-relaxed text-gray-300">
                {verifyEmailModalText}
              </p>
              <div className="mt-5 flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowVerifyEmailModal(false)}
                  className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-600"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
